import MDModal from "components/MDModal";
import SoftBox from "components/SoftBox";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";
import { usePromptLabels } from "queries/promptLabels";
import { useSaveFreeAccountTemplate } from "queries/accounts";
import { useAgencyInfo } from "context/agency";
import ReactSelect from "react-select";
import ModelPromptslabels from "../../../UiPrompts/promptlabels/ModelPrompts/Create";
import { Switch, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const { GET, POST } = ReqMethods;

export function ModalComponent({ open, onClose, handleRefresh, title, templateData }) {
  const request = useRequest();
  const [agency] = useAgencyInfo();
  const [openWarningModel, setOpenWarningModel] = useState(false);
  const [errors, setErrors] = useState({});
  const [tags, setTags] = useState([]);
  const [addTagModel, setAddTagModel] = useState(false);

  const isEditMode = !!(templateData && templateData._id);

  // Form state
  const [addTeamMember, setAddTeamMember] = useState(false);
  const [unlimitedTasks, setUnlimitedTasks] = useState(false);
  const [customAIAgent, setCustomAIAgent] = useState(false);
  const [unlimitedAIAgents, setUnlimitedAIAgents] = useState(false);
  const [createWorkspaces, setCreateWorkspaces] = useState(false);

  // Prefill toggle states + tags from templateData (edit mode)
  useEffect(() => {
    if (templateData) {
      setAddTeamMember(!!templateData.can_add_team_members);
      setUnlimitedTasks(!!templateData.unlimited_tasks);
      setCustomAIAgent(!!templateData.can_build_custom_ai_agents);
      setUnlimitedAIAgents(!!templateData.unlimited_ai_agents);
      setCreateWorkspaces(!!templateData.create_additional_workspaces);
      setTags(
        Array.isArray(templateData.promptlabels_ids) ? templateData.promptlabels_ids : []
      );
    } else {
      setAddTeamMember(false);
      setUnlimitedTasks(false);
      setCustomAIAgent(false);
      setUnlimitedAIAgents(false);
      setCreateWorkspaces(false);
      setTags([]);
    }
    setErrors({});
  }, [templateData, open]);

  const { data: tagsoption = [] } = usePromptLabels({ agencyId: agency._id, type: "agency" });

  const handleChangeTags = (selectedOptions) => {
    setTags(selectedOptions ? selectedOptions?.map((option) => option.value) : []);
  };

  // saas domain - not an Accounts hook (another fork owns queries/saas.js
  // territory), so inlined directly via useRequest() rather than centralized.
  const { data: products_data = null } = useQuery({
    queryKey: ["agency-free-saas", agency._id],
    queryFn: async () => {
      const { response } = await request(GET, "/saas/agencysaas", {
        query: `agency_id=${agency._id}`,
        toaster: false,
      });
      if (response && response.data) {
        const freeSaasItem = response.data.filter((item) => item?.is_free_saas === true)[0];
        return freeSaasItem?._id || null;
      }
      return null;
    },
    enabled: !!agency._id,
  });

  const validateForm = (form) => {
    const newErrors = {};
    if (!form.template_name.value) {
      newErrors.template_name = "Template Name is required";
    }
    return newErrors;
  };

  const { mutateAsync: saveTemplate, isPending: accountLoading } = useSaveFreeAccountTemplate(
    isEditMode,
    templateData?._id,
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm(e.target);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      template_name: e.target.template_name.value,
      description: e.target.description?.value || "",
      agency_id: agency._id,
      apps: ["Your AI"],
      type: "free",
      promptlabels_ids: tags,
      plan_id: agency.plan_id,

      can_add_team_members: addTeamMember,
      number_of_team_member: addTeamMember
        ? parseInt(e.target.number_of_team_member?.value) || 0
        : 0,

      unlimited_tasks: unlimitedTasks,
      number_of_tokens: unlimitedTasks
        ? 0
        : parseInt(e.target.number_of_tokens?.value) || 0,

      can_build_custom_ai_agents: customAIAgent,
      unlimited_ai_agents: customAIAgent ? unlimitedAIAgents : false,
      number_of_ai_agents:
        customAIAgent && !unlimitedAIAgents
          ? parseInt(e.target.number_of_ai_agents?.value) || 0
          : 0,

      create_additional_workspaces: createWorkspaces,
      number_of_additional_workspaces: createWorkspaces
        ? parseInt(e.target.number_of_additional_workspaces?.value) || 0
        : 0,
    };

    const result = await saveTemplate(payload);
    if (result.response) {
      onClose();
    }
    if (result.error) {
      window.alert(
        result.error.message || (isEditMode ? "Failed to update template" : "Failed to create template")
      );
    }
  };

  const handleclose = () => setAddTagModel(false);

  // Inlined rather than queries/promptLabels.js's useCreatePromptLabel - that
  // hook doesn't expose a toaster option, and this call site needs
  // toaster:true preserved exactly as before.
  const savedataPromptLabel = async (formData) => {
    const payload = { ...formData, agency_id: agency._id, type: "agency" };
    const { response } = await request(POST, "/promptslabel/create", { payload, toaster: true });
    if (response) {
      handleclose();
      setAddTagModel(false);
      promptslabel();
    }
  };

  const formColumnStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "100%",
  };

  return (
    <>
      <MDModal open={openWarningModel} onClose={() => setOpenWarningModel(false)}>
        Already Reached your Limits to save Accounts
      </MDModal>
      {open && (
        <div className="modal-zz-confirm text-left">
          <div
            className="scrollable-modal-content bg-white rounded"
            style={{ position: "relative" }}
          >
            <h2 className="text-black text-left font-14">
              {" "}
              {title || (isEditMode ? "Edit Template" : "Create Template")}{" "}
            </h2>
            <span className="crossison" onClick={onClose}>
              X
            </span>
            <SoftBox component="form" onSubmit={handleSubmit} key={templateData?._id || "new"}>
              <div style={formColumnStyle}>
                {/* Template Name */}
                <SoftBox className="mt-2">
                  <label className="text-black">Template Name</label>
                  <input
                    className="form-control-agust"
                    placeholder="Template Name"
                    name="template_name"
                    defaultValue={templateData?.template_name || ""}
                  />
                  {errors.template_name && (
                    <span style={{ color: "red" }}>{errors.template_name}</span>
                  )}
                </SoftBox>

                {/* Description */}
                <SoftBox>
                  <label className="text-black">Description</label>
                  <textarea
                    className="form-control-agust"
                    placeholder="Description"
                    name="description"
                    defaultValue={templateData?.description || ""}
                    style={{ minHeight: "90px", resize: "vertical" }}
                  />
                </SoftBox>

                <SoftBox>
                  <label className="text-black">Select Apps</label>
                  <div className="text-left" style={{ width: "100%", marginTop: "10px" }}>
                    <div className="d-flex justify-between card_agust_row">
                      <div>
                        <p className="font-16 text-black">Your AI</p>
                      </div>
                      <div>
                        <Switch checked={true} disabled />
                      </div>
                    </div>
                  </div>
                </SoftBox>

                <SoftBox>
                  <label className="text-black">Select AI Agent templates</label>
                  <div style={{ marginTop: "8px" }}>
                    <ReactSelect
                      name="tags"
                      placeholder="Select AI Agent templates"
                      className="form-input w-full"
                      options={
                        Array.isArray(tagsoption)
                          ? tagsoption.map((item) => ({
                            label: item.name,
                            value: item._id,
                          }))
                          : []
                      }
                      defaultValue={
                        Array.isArray(templateData?.promptlabels_ids) &&
                          Array.isArray(tagsoption)
                          ? tagsoption
                            .filter((item) =>
                              templateData.promptlabels_ids.includes(item._id)
                            )
                            .map((item) => ({ label: item.name, value: item._id }))
                          : []
                      }
                      isMulti
                      onChange={handleChangeTags}
                    />
                  </div>
                </SoftBox>

                {/* Can add team members */}
                <div className="text-left" style={{ width: "100%" }}>
                  <div className="d-flex justify-between card_agust_row">
                    <div>
                      <p className="font-16 text-black">Can add team members</p>
                    </div>
                    <div>
                      <Switch
                        checked={addTeamMember}
                        onChange={(e) => setAddTeamMember(e.target.checked)}
                      />
                    </div>
                  </div>
                  {addTeamMember && (
                    <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                      <label
                        htmlFor="number_of_team_member"
                        className="block text-black mb-2"
                        style={{ marginLeft: "4px" }}
                      >
                        How many team members can be added to this account
                      </label>
                      <p className="font-15 mb-2" style={{ marginLeft: "4px" }}>

                      </p>
                      <input
                        type="number"
                        id="number_of_team_member"
                        className="form-control-agust w-full"
                        name="number_of_team_member"
                        placeholder="Enter number of team members"
                        min="0"
                        defaultValue={templateData?.number_of_team_member || 0}
                      />
                    </div>
                  )}
                </div>

                {/* Unlimited AI Tasks */}
                <div className="text-left" style={{ width: "100%" }}>
                  <div className="d-flex justify-between card_agust_row">
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <p className="font-16 text-black">Unlimited AI Tasks</p>
                        <Tooltip
                          title="When enabled, this template will have no monthly AI Task limit"
                          arrow
                        >
                          <InfoOutlinedIcon
                            style={{ fontSize: "16px", color: "#6B7280", cursor: "pointer" }}
                          />
                        </Tooltip>
                      </div>
                    </div>
                    <div>
                      <Switch
                        checked={unlimitedTasks}
                        onChange={(e) => setUnlimitedTasks(e.target.checked)}
                      />
                    </div>
                  </div>
                  {!unlimitedTasks && (
                    <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                      <label
                        htmlFor="number_of_tokens"
                        className="block text-black mb-2"
                        style={{ marginLeft: "4px" }}
                      >
                        AI Tasks Monthly Limit
                      </label>

                      <input
                        type="number"
                        id="number_of_tokens"
                        className="form-control-agust w-full"
                        name="number_of_tokens"
                        placeholder="Number of monthly AI Tasks"
                        min="0"
                        defaultValue={templateData?.number_of_tokens || ""}
                      />
                    </div>
                  )}
                </div>

                {/* Can build custom AI Agents */}
                <div className="text-left" style={{ width: "100%" }}>
                  <div className="d-flex justify-between card_agust_row">
                    <div>
                      <p className="font-16 text-black">Can build Custom AI Agents</p>
                    </div>
                    <div>
                      <Switch
                        checked={customAIAgent}
                        onChange={(e) => setCustomAIAgent(e.target.checked)}
                      />
                    </div>
                  </div>
                  {customAIAgent && (
                    <>
                      {/* Unlimited AI Agents */}
                      <div className="text-left" style={{ width: "100%", marginTop: "10px" }}>
                        <div className="d-flex justify-between card_agust_row">
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <p className="font-16 text-black">Unlimited AI Agents</p>
                              <Tooltip
                                title="When enabled, this template will have no limit on AI Agents"
                                arrow
                              >
                                <InfoOutlinedIcon
                                  style={{ fontSize: "16px", color: "#6B7280", cursor: "pointer" }}
                                />
                              </Tooltip>
                            </div>
                          </div>
                          <div>
                            <Switch
                              checked={unlimitedAIAgents}
                              onChange={(e) => setUnlimitedAIAgents(e.target.checked)}
                            />
                          </div>
                        </div>
                      </div>
                      {!unlimitedAIAgents && (
                        <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                          <label
                            htmlFor="number_of_ai_agents"
                            className="block text-black mb-2"
                            style={{ marginLeft: "4px" }}
                          >
                            Number of AI Agents
                          </label>

                          <input
                            type="number"
                            id="number_of_ai_agents"
                            className="form-control-agust w-full"
                            name="number_of_ai_agents"
                            placeholder="Number of AI Agents this account can create"
                            min="0"
                            defaultValue={templateData?.number_of_ai_agents || ""}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Create additional workspaces */}
                <div className="text-left" style={{ width: "100%" }}>
                  <div className="d-flex justify-between card_agust_row">
                    <div>
                      <p className="font-16 text-black">Create additional workspaces</p>
                    </div>
                    <div>
                      <Switch
                        checked={createWorkspaces}
                        onChange={(e) => setCreateWorkspaces(e.target.checked)}
                      />
                    </div>
                  </div>
                  {createWorkspaces && (
                    <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                      <label
                        htmlFor="number_of_additional_workspaces"
                        className="block text-black mb-2"
                        style={{ marginLeft: "4px" }}
                      >
                        additional workspaces

                      </label>

                      <input
                        type="number"
                        id="number_of_additional_workspaces"
                        className="form-control-agust w-full"
                        name="number_of_additional_workspaces"
                        placeholder="Number of additional workspaces this account can create"
                        min="0"
                        defaultValue={templateData?.number_of_additional_workspaces || ""}
                      />
                    </div>
                  )}
                </div>
              </div>

              <SoftBox mt={4}>
                <button
                  disabled={accountLoading}
                  className="savebtn-setting w-100"
                  type="submit"
                  color="dark"
                  fullWidth
                >
                  {accountLoading
                    ? isEditMode
                      ? " Updating..."
                      : " Saving..."
                    : isEditMode
                      ? " Update Template"
                      : " Save Template"}
                </button>
              </SoftBox>
            </SoftBox>
          </div>
        </div>
      )}

      {addTagModel && (
        <ModelPromptslabels
          data={null}
          modelfunctionality={savedataPromptLabel}
          openModel={addTagModel}
          onclosemodel={handleclose}
        />
      )}
    </>
  );
}

ModalComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func,
  title: PropTypes.string,
  templateData: PropTypes.object,
};

function Modal({ handleRefresh }) {
  const [openAddMember, setOpenAddMember] = useState(false);

  return (
    <>
      <button className="btn btn-outline-dark" onClick={() => setOpenAddMember(true)}>
        Create Template
      </button>
      <ModalComponent
        open={openAddMember}
        onClose={() => setOpenAddMember(false)}
        handleRefresh={handleRefresh}
      />
    </>
  );
}

Modal.propTypes = {
  handleRefresh: PropTypes.func,
};

export default Modal;
